import React, { useEffect } from "react";
import Modal from "components/modal";
import InputField from "components/fields/InputField";
import FileInputField from "components/fields/FileInputField";
import Button from "components/button";
import { useForm } from "react-hook-form";
import { mediaUpload } from "api/usersApi";
import { showToast } from "components/toast";
import { ORDER_TYPE } from "constants";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function AddItemModal({
  isOpen,
  onClose,
  onAddItem,
  orderType = ORDER_TYPE.BASED_ON_DONE,
}) {
  const defaultValuesBased = {
    name: "",
    description: "",
    quantity: "",
    imgFile: null,
  };

  const defaultValuesCarrying = {
    name: "",
    imgFile: null,
    description: "",
    marka: "",
    ctn: "",
    qty: "",
    totalQty: "", // auto
    rate: "",
    totalAmount: "", // auto
    cbm: "",
    totalCBM: "", // auto
    kg: "",
    totalKg: "", // auto
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues:
      orderType === ORDER_TYPE.CARRYING
        ? defaultValuesCarrying
        : defaultValuesBased,
    mode: "onBlur",
    shouldUnregister: false,
  });

  useEffect(() => {
    reset(
      orderType === ORDER_TYPE.CARRYING
        ? defaultValuesCarrying
        : defaultValuesBased
    );
  }, [orderType, isOpen]);

  const wCtn = watch("ctn");
  const wQty = watch("qty");
  const wRate = watch("rate");
  const wCbm = watch("cbm");
  const wKg = watch("kg");

  // Auto-calculations
  useEffect(() => {
    if (orderType !== ORDER_TYPE.CARRYING) return;
    setValue("totalQty", num(wCtn) * num(wQty));
  }, [orderType, wCtn, wQty, setValue]);

  useEffect(() => {
    if (orderType !== ORDER_TYPE.CARRYING) return;
    setValue("totalAmount", num(wRate) * num(wCtn) * num(wQty));
  }, [orderType, wRate, wCtn, wQty, setValue]);

  useEffect(() => {
    if (orderType !== ORDER_TYPE.CARRYING) return;
    setValue("totalCBM", num(wCbm) * num(wCtn));
  }, [orderType, wCbm, wCtn, setValue]);

  useEffect(() => {
    if (orderType !== ORDER_TYPE.CARRYING) return;
    setValue("totalKg", num(wKg) * num(wCtn));
  }, [orderType, wKg, wCtn, setValue]);

  const handleSave = async (data) => {
    try {
      let imageUrl = null;

      if (data.imgFile && data.imgFile[0]) {
        const res = await mediaUpload(data.imgFile[0]);
        if (res?.statuscode === 200 || res?.statuscode === 201) {
          imageUrl = res?.data;
        } else {
          console.error("Image upload failed:", res?.msg || res);
          showToast.error("Image upload failed");
          return;
        }
      }

      if (orderType === ORDER_TYPE.CARRYING) {
        const newItem = {
          name: data.name,
          description: data.description,
          marka: data.marka,
          ctn: num(data.ctn),
          qty: num(data.qty), // per CTN
          totalQty: num(data.totalQty),
          rate: num(data.rate),
          totalAmount: num(data.totalAmount),
          cbm: num(data.cbm), // per CTN
          totalCBM: num(data.totalCBM),
          kg: num(data.kg), // per CTN
          totalKg: num(data.totalKg),
          picture: imageUrl,
        };

        onAddItem(newItem);
        reset(defaultValuesCarrying);
      } else {
        const newItem = {
          name: data.name,
          description: data.description,
          totalQty: num(data.quantity), // NOTE: API expects 'qty'
          picture: imageUrl,
        };

        onAddItem(newItem);
        reset(defaultValuesBased);
      }

      onClose();
      showToast.success("Item added successfully");
    } catch (e) {
      console.error("Add items failed:", e);
    }
  };
  const ReadonlyBox = ({ label, value, id }) => {
    const base =
      "mt-0.5 flex h-9 w-full rounded-md border bg-white/0 p-3 text-sm outline-none border-gray-200 dark:!border-white/10 dark:text-white";
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white"
          >
            {label}
          </label>
        )}
        <div id={id} className={base}>
          {value ?? 0}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={`${
        orderType === ORDER_TYPE.CARRYING ? "!max-w-6xl" : "!max-w-3xl"
      }`}
    >
      <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
        {orderType === ORDER_TYPE.CARRYING ? "Add Item (Carrying)" : "Add Item"}
      </h2>

      <form onSubmit={handleSubmit(handleSave)} className="mt-4">
        {orderType === ORDER_TYPE.CARRYING ? (
          <div className="grid grid-cols-1 gap-4">
            {" "}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <FileInputField
                id="imgFile"
                label="Upload Image*"
                accept="image/*"
                error={errors.imgFile}
                {...register("imgFile", { required: "Image is required" })}
              />
              <InputField
                id="name"
                label="Name*"
                variant="auth"
                type="text"
                placeholder="e.g., Phone"
                error={errors.name}
                {...register("name", { required: "Name is required" })}
              />

              <InputField
                id="description"
                label="Description*"
                variant="auth"
                type="text"
                placeholder="Item description"
                error={errors.description}
                {...register("description")}
              />

              <InputField
                id="marka"
                label="Marka*"
                variant="auth"
                type="text"
                placeholder="Brand / Marka"
                error={errors.marka}
                {...register("marka", { required: "Marka is required" })}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
              <InputField
                id="ctn"
                label="CTN*"
                variant="auth"
                type="number"
                placeholder="0"
                error={errors.ctn}
                {...register("ctn", {
                  required: "CTN is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="qty"
                label="QTY (per CTN)*"
                variant="auth"
                type="number"
                placeholder="0"
                error={errors.qty}
                {...register("qty", {
                  required: "QTY is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="rate"
                label="Rate*"
                variant="auth"
                type="number"
                step="0.01"
                placeholder="0.00"
                error={errors.rate}
                {...register("rate", {
                  required: "Rate is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="cbm"
                label="CBM (per CTN)*"
                variant="auth"
                type="number"
                step="0.0001"
                placeholder="0.0000"
                error={errors.cbm}
                {...register("cbm", {
                  required: "CBM is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />

              <InputField
                id="kg"
                label="KG (per CTN)*"
                variant="auth"
                type="number"
                step="0.01"
                placeholder="0.00"
                error={errors.kg}
                {...register("kg", {
                  required: "KG is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Must be ≥ 0" },
                })}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
            {/* Readonly values section at bottom */}
            <div className=" grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <ReadonlyBox label="Total QTY (auto)" value={watch("totalQty")} />
              <input
                id="totalQty"
                type="hidden"
                {...register("totalQty", { valueAsNumber: true })}
              />

              <ReadonlyBox
                label="Total Amount (auto)"
                value={watch("totalAmount")}
              />
              <input
                id="totalAmount"
                type="hidden"
                {...register("totalAmount", { valueAsNumber: true })}
              />

              <ReadonlyBox label="Total CBM (auto)" value={watch("totalCBM")} />
              <input
                id="totalCBM"
                type="hidden"
                {...register("totalCBM", { valueAsNumber: true })}
              />

              <ReadonlyBox label="Total KG (auto)" value={watch("totalKg")} />
              <input
                id="totalKg"
                type="hidden"
                {...register("totalKg", { valueAsNumber: true })}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* FILE (required for base mode, per your original) */}
            <FileInputField
              id="imgFile"
              label="Upload Image*"
              accept="image/*"
              error={errors.imgFile}
              {...register("imgFile", { required: "Image is required" })}
            />

            {/* TEXT FIELDS (required) */}
            <InputField
              id="name"
              label="Item Name*"
              type="text"
              variant="auth"
              error={errors.name}
              {...register("name", {
                required: "Item name is required",
                setValueAs: (v) => v?.trim(),
              })}
            />

            <InputField
              id="description"
              label="Description*"
              type="text"
              variant="auth"
              placeholder="Enter description"
              error={errors.description}
              {...register("description", {
                setValueAs: (v) => v?.trim(),
              })}
            />

            {/* NUMERIC FIELDS (required) */}
            <InputField
              id="quantity"
              label="Quantity*"
              type="number"
              variant="auth"
              error={errors.quantity}
              {...register("quantity", {
                required: "Quantity is required",
                valueAsNumber: true,
                min: { value: 1, message: "Quantity must be at least 1" },
              })}
            />
          </div>
        )}

        <div className="col-span-2 my-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Save Item</Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddItemModal;
